// Candidate presentations (JT1-1886). These mirror the backend's
// modules/tenant/candidates/presentations/types/presentation.types.ts; the server validates every save.

export enum PresentationKind {
  COVER_PLUS_RESUME = 'cover_plus_resume',
  FORMATTED_RESUME = 'formatted_resume',
  PROFILE = 'profile',
  EXECUTIVE = 'executive',
}

export enum PresentationExportFormat {
  PDF = 'pdf',
  DOCX = 'docx',
}

export type PresentationFieldKey =
  | 'candidate.full_name'
  | 'candidate.first_name'
  | 'candidate.position_title'
  | 'candidate.email'
  | 'candidate.phone'
  | 'candidate.location'
  | 'candidate.linkedin'
  | 'candidate.availability'
  | 'candidate.notice_period'
  | 'candidate.salary_expectation'
  | 'candidate.current_employer'
  | 'job.title'
  | 'client.name'
  | 'recruiter.name'
  | 'recruiter.email'
  | 'recruiter.phone'
  | 'agency.name'
  | 'agency.phone'
  | 'agency.website'
  | 'document.date';

/** The merge fields offered as chips, most used first. */
export const MERGE_FIELD_KEYS: PresentationFieldKey[] = [
  'candidate.full_name',
  'candidate.position_title',
  'client.name',
  'job.title',
  'recruiter.name',
  'document.date',
  'candidate.location',
  'candidate.availability',
  'candidate.notice_period',
  'candidate.salary_expectation',
  'agency.name',
];

export interface PageSettings {
  size: 'A4' | 'LETTER';
  margins_mm: { top: number; right: number; bottom: number; left: number };
}

export type ImageRef = 'brand' | 'none' | { file_id: string };

export interface ThemeTokens {
  font_family: string;
  base_size_pt: number;
  colors: { primary: string; secondary: string; text: string; muted: string };
  heading: { style: 'plain' | 'underline' | 'bar' | 'filled'; uppercase: boolean };
  header: { image: ImageRef; logo: 'left' | 'right' | 'none'; text?: string | null };
  footer: { image: ImageRef; text?: string | null; page_numbers: 'none' | 'n' | 'n_of_total'; align: 'left' | 'center' | 'right' };
  confidentiality_text?: string | null;
}

export type BlockSource = 'summary' | 'work_experiences' | 'education_history' | 'skills' | 'certifications' | 'industries' | 'contact' | 'cover';

interface BlockBase {
  id: string;
  hidden?: boolean;
  hide_when_empty?: boolean;
  title?: string | null;
  page_break_before?: boolean;
  keep_together?: boolean;
  source?: BlockSource | null;
  origin?: 'template' | 'profile' | 'manual' | 'ai';
  ai_pending?: boolean;
}

export interface FieldRow { label: string; value: string }
export interface MonthYear { month?: number | null; year?: number | null }

export interface EmploymentItem {
  id: string;
  source_id?: string | null;
  hidden?: boolean;
  employer: string;
  position_title: string;
  location?: string | null;
  start: MonthYear;
  end: MonthYear | null;
  summary_html: string;
}

export interface EducationItem {
  id: string;
  source_id?: string | null;
  hidden?: boolean;
  institution: string;
  qualification: string;
  level?: string | null;
  start_year?: number | null;
  end_year?: number | null;
}

export type PresentationBlock =
  | (BlockBase & { type: 'cover'; heading: string; subheading?: string | null; rows: FieldRow[]; show_logo: boolean; confidentiality_text?: string | null })
  | (BlockBase & { type: 'heading'; text: string; level: 1 | 2 | 3 })
  | (BlockBase & { type: 'rich_text'; role?: 'summary' | 'commentary' | 'free'; html: string })
  | (BlockBase & { type: 'field_list'; layout: 'inline' | 'two_column'; rows: FieldRow[] })
  | (BlockBase & { type: 'employment'; date_format: 'MMM YYYY' | 'YYYY'; items: EmploymentItem[] })
  | (BlockBase & { type: 'education'; items: EducationItem[] })
  | (BlockBase & { type: 'list'; columns: 1 | 2 | 3 | 4; style: 'bullets' | 'plain'; items: string[] })
  | (BlockBase & { type: 'table'; preset?: 'languages' | null; columns: string[]; rows: string[][]; header_row: boolean })
  | (BlockBase & { type: 'image'; file_id: string; width_pct: number; align: 'left' | 'center' | 'right' })
  | (BlockBase & { type: 'divider' })
  | (BlockBase & { type: 'page_break' })
  | (BlockBase & { type: 'original_resume'; candidate_document_id: string | null });

export type PresentationBlockType = PresentationBlock['type'];

export interface PresentationContent {
  schema_version: number;
  page: PageSettings;
  theme_overrides?: Partial<ThemeTokens> | null;
  fields: Partial<Record<PresentationFieldKey, string | null>>;
  snapshot: { source_profile_id: string | null; taken_at: string };
  blocks: PresentationBlock[];
}

export type AnonymisationLevel = 'basic' | 'standard' | 'strict';

/** What an AI draft of a text section does (backend ai/presentation-ai.provider.ts). */
export type SummaryAction = 'generate' | 'tailor_to_job' | 'shorten' | 'expand' | 'improve';
export type ReplacementKind = 'name' | 'email' | 'phone' | 'url' | 'photo' | 'employer' | 'project' | 'location' | 'custom';
export type ReplacementStatus = 'suggested' | 'accepted' | 'edited' | 'rejected';

export interface Replacement {
  id: string;
  kind: ReplacementKind;
  original: string;
  replacement: string;
  status: ReplacementStatus;
  source: 'rule' | 'ai' | 'manual';
}

export interface ScanFinding {
  block_id: string | null;
  kind: ReplacementKind | 'metadata' | 'flag';
  text: string;
  location: string;
}

export interface AnonymisationState {
  enabled: boolean;
  level: AnonymisationLevel;
  alias: string;
  replacements: Replacement[];
  reviewed_flags?: string[];
  last_scan?: { at: string; findings: ScanFinding[] } | null;
}

export interface PresentationPerson { id: string; first_name: string; last_name: string }

export interface PresentationExport {
  id: string;
  format: PresentationExportFormat;
  presentation_version: number;
  anonymised: boolean;
  file_name: string;
  findings_acknowledged: boolean;
  created_at: string;
  created_by: PresentationPerson | null;
}

export interface PresentationSummary {
  id: string;
  title: string;
  kind: PresentationKind;
  template_key: string;
  version: number;
  job: { id: string; title: string } | null;
  application_id: string | null;
  anonymised: boolean;
  created_at: string;
  updated_at: string;
  updated_by: PresentationPerson | null;
  export_count: number;
  latest_export: PresentationExport | null;
  exports: PresentationExport[];
}

export interface Presentation extends PresentationSummary {
  organization_candidate_id: string;
  content: PresentationContent;
  anonymisation: AnonymisationState;
  snapshot_taken_at: string;
  profile_changed: boolean;
}

export interface PresentationTemplateSummary {
  key: string;
  name: string;
  description: string | null;
  kind: PresentationKind;
  source: 'system' | 'agency';
  is_default: boolean;
  anonymise_by_default: boolean;
  updated_at: string | null;
}

export interface TemplateDefinition {
  schema_version: number;
  kind: PresentationKind;
  page: PageSettings;
  theme: ThemeTokens;
  blocks: PresentationBlock[];
}

export interface PresentationTemplate extends PresentationTemplateSummary {
  definition: TemplateDefinition;
}

export interface CreatePresentationRequest {
  candidateId: string;
  template_key?: string;
  title?: string;
  application_id?: string;
  anonymise?: boolean;
}

export interface UpdatePresentationRequest {
  id: string;
  version: number;
  title?: string;
  content?: PresentationContent;
  anonymisation?: AnonymisationState;
}

export interface ExportPresentationRequest {
  id: string;
  format: PresentationExportFormat;
  version: number;
  acknowledge_findings?: boolean;
}

export interface ExportPresentationResult {
  id: string;
  format: PresentationExportFormat;
  file_name: string;
  anonymised: boolean;
  presentation_version: number;
  findings: ScanFinding[];
  skipped_attachments: string[];
  download_url: string;
  created_at: string;
}

/** Error codes the builder reacts to (backend presentation.errors.ts). */
export const PresentationErrorCodes = {
  StaleVersion: '29_0101',
  ExportNotAcknowledged: '29_0103',
  RenderBusy: '29_0001',
  AiUnavailable: '29_0106',
  AiFailed: '29_0107',
  AiDraftPending: '29_0108',
} as const;
